// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import {
  spacing,
  fontSize,
  mediaMin,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import { black, gray04, primary } from 'presentations/views/styles/color';
import CalendarPageTemplate from 'presentations/views/components/templates/apps/CalendarPageTemplate';
import Breadcrumb from 'presentations/routes/breadcrumb';
import { Gengo } from 'usecases/app_calendar/jp/gengo';
import { formatGengoYear } from 'usecases/app_calendar/jp/gengo/formatter';
import { appCalendarPath } from 'presentations/routes/path';

interface Props {
  routes?: Breadcrumb[];
  gengos: Gengo[];
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  return (
    <CalendarPageTemplate routes={props.routes}>
      <MainContents>
        <Content>
          <ListContainer>
            <Headline>{t('title')}</Headline>
            <LinkListContainer>
              <dl>
                {props.gengos.map((gengo, index) => {
                  const items = [];
                  for (let i = gengo.start; i <= gengo.end; i++) {
                    items.push(
                      <LinkListItem key={i}>
                        <LinkListItemLink
                          href={appCalendarPath(
                            {
                              year: i,
                            },
                            router.locale,
                          )}
                        >
                          {i}年（{gengo.name}
                          {formatGengoYear(i - gengo.start + 1)}年）
                        </LinkListItemLink>
                      </LinkListItem>,
                    );
                  }
                  return (
                    <LinkListInner key={index}>
                      <LinkListTitle>{gengo.name}</LinkListTitle>
                      <LinkListContent>
                        <LinkList>{items}</LinkList>
                      </LinkListContent>
                    </LinkListInner>
                  );
                })}
              </dl>
            </LinkListContainer>
          </ListContainer>
        </Content>
      </MainContents>
    </CalendarPageTemplate>
  );
}

const LinkListContainer = styled.div`
  ${mediaMin.tablet} {
    border: 1px solid ${gray04};
    padding: ${spacing(24)} ${spacing(32)} ${spacing(12)};
    border-radius: ${borderRadius()};
  }
`;
const LinkListTitle = styled.dt`
  font-size: ${fontSize(14)};
  flex-shrink: 0;
  width: 5em;
  padding-top: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
    padding-top: 0;
    padding-right: 1em;
  }
  ${mediaMax.xs} {
    width: 4em;
  }
`;
const LinkListInner = styled.div`
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
    display: flex;
  }
  :not(:first-of-type) {
    ${mediaMax.tablet} {
      border-top: 1px solid ${gray04};
    }
    ${mediaMin.tablet} {
      margin-top: ${fontSize(12)};
    }
  }
`;
const LinkListContent = styled.dd`
  width: 100%;
  padding: ${spacing(16)} 0 0;
  ${mediaMin.tablet} {
    padding: 0;
  }
`;
const LinkList = styled.ul`
  display: flex;
  flex-wrap: wrap;
`;
const LinkListItem = styled.li`
  font-size: ${fontSize(14)};
  padding-right: ${spacing(24)};
  margin-bottom: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
    margin-bottom: ${spacing(12)};
  }
`;
const LinkListItemLink = styled.a`
  color: ${primary};
  ${mediaMax.tablet} {
    display: block;
  }
  &:hover {
    color: ${primary};
    text-decoration: underline;
  }
`;

const MainContents = styled.main`
  padding: ${spacing(40)} ${spacing(16)};
  ${mediaMin.tablet} {
    padding-top: ${spacing(64)};
  }
`;
const Content = styled.div`
  color: ${black};
  ${mediaMin.tablet} {
    max-width: 1100px;
    margin: 0 auto;
  }
`;
const Headline = styled.h2`
  font-size: ${fontSize(28)};
  margin-bottom: ${spacing(16)};
`;
const ListContainer = styled.div`
  margin-bottom: ${spacing(48)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;
